// modified based on https://github.com/rt2zz/redux-persist/blob/master/src/stateReconciler/autoMergeLevel2.js
import { omit, pick } from 'lodash'
import cloneDeep from 'lodash/cloneDeep'
import isArray from 'lodash/isArray'
import isEqual from 'lodash/isEqual'
import isPlainObject from 'lodash/isPlainObject'
import mergeWith from 'lodash/mergeWith'

import { GenericObject } from 'types'

function getColumnIdMinimumObject(columns: GenericObject[]): any[] {
    return columns.map((each) => ({
        id: each.id,
        isVisible: each.isVisible,
    }))
}

function mergeCustomizer(
    objValue: any, // Coming from memory
    srcValue: any, // Coming from storage
    key: string
): string[] | GenericObject | undefined {
    if (key === 'columns' && isArray(objValue)) {
        // We want the localStorage to keep the order and visibility of the columns as selected by the user.
        if (
            !isEqual(
                getColumnIdMinimumObject(objValue),
                getColumnIdMinimumObject(srcValue)
            )
        ) {
            const newColumns: any[] = []
            const storageColumnMap = srcValue.reduce(
                (acc: { [columnId: string]: any }, column: any) => {
                    acc[column.id] = column
                    return acc
                },
                {}
            )
            const defaultStateColumnMap = objValue.reduce(
                (acc: { [columnId: string]: any }, column: any) => {
                    acc[column.id] = column
                    return acc
                },
                {}
            )

            // Add columns that are in storage and default state
            Object.keys(storageColumnMap).forEach((columnId) => {
                if (defaultStateColumnMap[columnId]) {
                    newColumns.push(storageColumnMap[columnId])
                }
            })

            const prevColumnMap: { [key: string]: string | null } = {}
            let prevColumnId: string | null = null

            // Iterate over default state to create the map of previous columns
            Object.keys(defaultStateColumnMap).forEach((columnId) => {
                if (!storageColumnMap[columnId]) {
                    if (prevColumnId !== null) {
                        prevColumnMap[columnId] = prevColumnId
                    }
                }
                prevColumnId = columnId
            })

            // Use prevColumnMap to insert newly added columns after their previous columns
            Object.keys(prevColumnMap).forEach((columnId) => {
                prevColumnId = prevColumnMap[columnId]
                const prevIndex = newColumns.findIndex(
                    (column) => column.id === prevColumnId
                )
                if (prevIndex !== -1) {
                    newColumns.splice(
                        prevIndex + 1,
                        0,
                        defaultStateColumnMap[columnId]
                    )
                } else {
                    newColumns.push(defaultStateColumnMap[columnId])
                }
            })

            return newColumns
        }
    }

    if (key === 'pagination') {
        return mergeWith(objValue, { pageSize: srcValue.pageSize })
    }

    if (key === 'filtersSettings') {
        // Legacy keys, always take the memory values
        const exclusiveKeys = ['anchored', 'order', 'displayState']
        const exclusivePart = pick(objValue, exclusiveKeys)

        const objOmitted = omit(objValue, exclusiveKeys)
        const srcOmitted = omit(srcValue, exclusiveKeys)

        return {
            ...mergeWith(objOmitted, srcOmitted),
            ...exclusivePart,
        }
    }

    /**
     * Here we are always taking the sorter value from localStorage
     * otherwise lodash/mergeWith will always combine the default and localStorage
     * arrays rather than take the most recent change over the default.
     */
    if (key === 'sorter') {
        return srcValue
    }

    return undefined
}

export default function autoMerge(
    inboundState: GenericObject,
    originalState: GenericObject,
    reducedState: GenericObject,
    { debug }: GenericObject
): GenericObject {
    const newState = cloneDeep(reducedState)

    // Only rehydrate if inboundState exists and is an object
    if (inboundState && typeof inboundState === 'object') {
        Object.keys(inboundState).forEach((key) => {
            // Ignore _persist data
            if (key === '_persist') {
                return
            }

            // If reducer modifies sub state, skip auto rehydration
            if (!isEqual(originalState[key], reducedState[key])) {
                if (process.env.NODE_ENV !== 'production' && debug) {
                    // eslint-disable-next-line no-console
                    console.log(
                        'redux-persist/stateReconciler: key `%s` had its sub state modified.',
                        key
                    )
                }
                return
            } else if (debug) {
                // eslint-disable-next-line no-console
                console.log(
                    'redux-persist/stateReconciler: key `%s` has originalState equal to reducedState',
                    key
                )
            }

            if (isPlainObject(reducedState[key])) {
                newState[key] = mergeWith(
                    newState[key],
                    inboundState[key],
                    mergeCustomizer
                )
                if (debug) {
                    // eslint-disable-next-line no-console
                    console.log(
                        'redux-persist/stateReconciler: key `%s` reconciled as object by `autoMerge`',
                        key
                    )
                }
                return
            }

            // Otherwise hard set
            newState[key] = inboundState[key]
        })
    }

    if (
        process.env.NODE_ENV !== 'production' &&
        debug &&
        inboundState &&
        typeof inboundState === 'object'
    ) {
        // eslint-disable-next-line no-console
        console.log(
            `redux-persist/stateReconciler: rehydrated keys '${Object.keys(
                inboundState
            ).join(', ')}'`
        )
    }

    return newState
}
